import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { loadTeamScores } from 'js/actions/rubric-actions';
import { useQuery } from 'js/hooks/useQuery';
import { QueryBase } from 'js/store/reducers/queries';
import RubricPreviewsComponent from '../components/RubricPreviews';
import { applyWeightSchemeToEvaluation } from 'js/utils/rubric';

const RUBRIC_TEAM_SCORES_IDLE_QUERY = `${QueryBase.RubricTeamScores}/__idle`;

function rubricTeamScoresQueryId(
  workspaceId,
  rubricId,
  teamId,
  showUnweighted
) {
  const uw = showUnweighted ? '1' : '0';
  return `${QueryBase.RubricTeamScores}/${workspaceId}/${rubricId}/${teamId}/${uw}`;
}

function RubricPreviewsContainer(props) {
  const { rubricId, teamId, rubric } = props;
  const dispatch = useDispatch();
  const { workspaceId } = useParams();
  const [showUnweighted, setShowUnweighted] = useState(true);
  const [weightScheme, setWeightScheme] = useState('');

  const queryId = useMemo(() => {
    if (!workspaceId || !rubricId || !teamId) {
      return RUBRIC_TEAM_SCORES_IDLE_QUERY;
    }
    return rubricTeamScoresQueryId(
      workspaceId,
      rubricId,
      teamId,
      showUnweighted
    );
  }, [workspaceId, rubricId, teamId, showUnweighted]);

  const queryError = useSelector((state) => state.queries[queryId]?.error);

  const loadQuery = useCallback(
    (qid) => {
      if (qid === RUBRIC_TEAM_SCORES_IDLE_QUERY) {
        return Promise.resolve(null);
      }
      return dispatch(
        loadTeamScores(rubricId, teamId, qid, {
          omitUnweighted: !showUnweighted,
        })
      );
    },
    [dispatch, rubricId, teamId, showUnweighted]
  );

  const {
    refresh,
    result: scoresData,
    isLoading,
    hasLoaded,
  } = useQuery({
    queryId,
    query: loadQuery,
  });

  useEffect(() => {
    if (queryId !== RUBRIC_TEAM_SCORES_IDLE_QUERY) {
      refresh();
    }
  }, [queryId, refresh]);

  const loadError = Boolean(queryError);
  const loading =
    queryId !== RUBRIC_TEAM_SCORES_IDLE_QUERY &&
    !loadError &&
    (isLoading || !hasLoaded);

  const displayedScoresData = useMemo(() => {
    if (!scoresData) return scoresData;
    // Scores arrive weighted by the team's saved scheme; "Default weights"
    // means the document's, so rebuild from those too.
    return applyWeightSchemeToEvaluation(
      scoresData,
      rubric?.document,
      String(weightScheme ?? '').trim(),
      { defaultWeightsFromDocument: true }
    );
  }, [scoresData, rubric?.document, weightScheme]);

  return (
    <RubricPreviewsComponent
      {...props}
      rubric={rubric}
      loading={loading}
      loadError={loadError}
      scoresData={displayedScoresData}
      showUnweighted={showUnweighted}
      onShowUnweightedChange={setShowUnweighted}
      weightScheme={weightScheme}
      onWeightSchemeChange={setWeightScheme}
    />
  );
}

export default RubricPreviewsContainer;
